/**
 * HydraClient — browser WebSocket to Hydra's consumer `/ws` (deployed API is typically port 8891, e.g. `ws://hydra.newbx.com:8891/ws`).
 * Protocol: Hydra repo `CONSUMER_API.md`.
 *
 * **Auth:** Hydra's `ConsumerWebSocketServer` validates `Authorization: Bearer <consumerBearerToken>`
 * when that env is set. The browser `WebSocket` API cannot set custom headers, so this client connects
 * with the URL only. Use an open Hydra (no consumer token), same-origin proxy, or a non-browser client
 * if your deployment requires Bearer on the socket.
 *
 * **Connection sharing:** When the browser supports `SharedWorker`, all g1xl tabs in the
 * same browser session share a single upstream WS via `src/workers/hydraWorker.ts`.
 * This satisfies Hydra's 1-WS-per-service rule (HYDRA_API.md:16-55). Tabs in browsers
 * without SharedWorker support fall back to one direct WS each (legacy behaviour).
 *
 * **Protocol (matches Hydra consumer docs):**
 *   Client → Server: { op: 'subscribe', channel: 'all' | 'markets' | 'orderbooks' }
 *                     { op: 'snapshot', channel: 'markets' | 'orderbooks' }
 *                     { op: 'ping', ts: number }
 *   Server → Client: market | orderbook | pong | backpressure | error
 */

import { connectMarketOps } from '../workers/sportFilter';

// ── Hydra message types ──────────────────────────────────────────

export interface HydraMarket {
  ticker: string;
  eventTicker: string;
  // Kalshi parent event series ticker, e.g. KXNBAWEST. Forwarded by newer
  // Hydra builds so consumers can classify sibling families without
  // hardcoding every raw market prefix.
  seriesTicker?: string;
  title: string;
  eventTitle?: string;
  // Per-market subtitle forwarded from Kalshi's `yes_sub_title`. Carries
  // the canonical line on spread + total frames — e.g. "Over 203.5 points
  // scored" or "Montreal wins by over 2.5 goals". Single source of truth
  // for line derivation; no per-sport bucket math needed when present.
  subtitle?: string;
  status: string;
  sport?: string;
  /** Scope of play. 'full' (default / full game) or a period: '1H' (1st half,
   *  Kalshi + Poly), '1Q'–'4Q' (single quarters, Kalshi-only), '2H' (soccer
   *  Game Lines), or 'F5' (MLB First 5 Innings, Kalshi-only). Set by hydra at
   *  discovery from the ticker so consumers bucket period markets WITHOUT
   *  re-parsing. Absent ⇒ treat as 'full'. See HYDRA_API.md "Period markets". */
  period?: 'full' | '1H' | '2H' | 'F5' | '1Q' | '2Q' | '3Q' | '4Q';
  category?: string;
  yesBid?: number;  // cents
  yesAsk?: number;  // cents
  noBid?: number;
  noAsk?: number;
  lastPrice?: number;
  volume?: number;
  openInterest?: number;
  // Scheduled start time of the parent event (game/match/fight), ISO UTC.
  // Sourced by Hydra from Polymarket Gamma `gameStartTime` and Kalshi
  // milestone `start_date` when available. With canonicalGameKey collapsing
  // venue siblings onto one row, one source can power the cross-venue display.
  // Undefined when upstream metadata has no start signal.
  eventStartTime?: string;
  source: 'kalshi' | 'polymarket';
  lastUpdateMs: number;
  // Hydra-normalized identifiers. Present on dan-dev+; undefined on older
  // Hydra builds — consumers must tolerate that. See hydra/HYDRA_API.md.
  canonicalGameKey?: string;   // "sport|YYYY-MM-DD|TEAM_A|TEAM_B", alpha-sorted
  canonicalAwayCode?: string;  // Kalshi only (Poly slugs order by YES-outcome)
  canonicalHomeCode?: string;  // Kalshi only
  // Source-agnostic alpha-sorted canonical pair. Set on both Kalshi and
  // Polymarket frames whenever the market parses into a game (per-day
  // moneyline / spread / total). Lets the row collapse off raw Poly slug
  // aliases (NYL→NY, POR→PDX, LAS→LV, GSV→GS for WNBA) even when Kalshi
  // hasn't listed the game yet. Not game-day-positional — use
  // canonicalAwayCode/canonicalHomeCode for that.
  canonicalTeams?: [string, string];
  // Soccer 3-way / future N-outcome markets: canonical outcome that this
  // binary market's YES side pays on. Team code or "DRAW". Kalshi's raw draw
  // suffix is TIE and Polymarket's is draw; Hydra normalizes both here.
  yesOutcome?: string;
  // Set on every market frame for series-related tickers (Kalshi
  // KX*SERIES*/KX*SERIESSCORE*/KX*SERIESROADWIN* and Polymarket
  // *-playoffs-who-will-win-series-* slugs). Shape:
  //   "{sport}|series|{YY}|TEAM_A-TEAM_B"
  // Teams alpha-sorted, normalized canonical 3-char codes. Used by
  // marketStore to collapse cross-venue series tickers onto one row,
  // mirroring the canonicalGameKey path for per-day games.
  canonicalSeriesKey?: string;
  // Set on series tickers (Kalshi + Polymarket): the canonical 3-char
  // team code whose win resolves the binary contract YES. On Polymarket
  // slugs the YES team is encoded by slug position (not alpha order),
  // so this is the only reliable per-team mapping for series rows.
  // Falls back to "row's awayCode is YES" when undefined.
  canonicalYesTeam?: string;
  // Set on every series-related ticker frame (KX*SERIES, KX*SERIESSCORE,
  // KX*SERIESROADWIN, Polymarket who-will-win-series slugs).
  //
  //   seriesWins         — per-team wins keyed by canonical 3-char team code,
  //                        e.g. {BOS: 1, PHI: 2}. Undefined on pre-deploy /
  //                        pre-tracker-warmup frames or Polymarket-only series
  //                        where there are no Kalshi GAME tickers to count.
  //   seriesWinsToClinch — wins required to take the series. 4 for best-of-7,
  //                        3 for best-of-5, 2 for best-of-3. Derived from
  //                        Kalshi's KX*SERIESSCORE outcome shape; falls back
  //                        to 4 (NBA/NHL playoffs are always best-of-7).
  //
  // Used by clinch-injection: skip unless some team is on match point
  // (wins ≥ seriesWinsToClinch − 1). Series-not-started and no-team-on-hill
  // both fall through to "leave Kalshi cells blank when per-day ticker is
  // missing" rather than mis-injecting the series-winner ticker.
  seriesWins?: Record<string, number>;
  seriesWinsToClinch?: number;
  // Polymarket-only token plumbing. Populated by hydra ≥ 2026-05-16 from
  // its tokenRegistry (Gamma `conditionId` + `clobTokenIds[0]=yes,
  // clobTokenIds[1]=no`). Required by g1's TradeModal to construct CLOB
  // orders without a second lookup at trade time. Older hydra builds
  // leave all three undefined; consumers must tolerate that.
  conditionId?: string;
  yesTokenId?: string;
  noTokenId?: string;
  /**
   * Polymarket-only. True when the market belongs to a NEG_RISK
   * multi-outcome event (mutually-exclusive bundled yes/no markets —
   * NBA Draft first pick, election primary slots, etc.). Forwarded to
   * placeOrder via the v2 SDK or signing fails with `invalid signature`.
   * Older hydra builds leave this undefined; consumers must tolerate
   * that (treat absent as false).
   */
  negRisk?: boolean;
  /**
   * Polymarket-only. CLOB minimum price increment as a stringified
   * decimal — "0.01" for most sports moneylines, "0.001"/"0.0001" for
   * fine-tick binaries. Sourced from Gamma `orderPriceMinTickSize` via
   * hydra's CachedMarket. Forwarded to placeOrder so the v2 CLOB
   * doesn't reject with `invalid tick size (X), minimum for the market
   * is Y` on markets with sub-cent increments. Older hydra builds
   * (pre-2026-05-23) leave this undefined; relay-side `placeOrder`
   * defaults to '0.01' in that case, which is correct for every sports
   * moneyline we trade but wrong for fine-tick binaries — pass the
   * value through whenever it's present.
   */
  tickSize?: string;
}

export interface HydraPriceLevel {
  price: number;  // cents 1-99
  size: number;
}

export interface HydraOrderbook {
  ticker: string;
  yes: HydraPriceLevel[];
  no: HydraPriceLevel[];
  timestamp: number;
}

export interface HydraMarketMsg {
  type: 'market';
  market: HydraMarket;
  serverTs: number;
  seq: number;
}

export interface HydraOrderbookMsg {
  type: 'orderbook';
  ticker: string;
  orderbook: HydraOrderbook;
  staleness?: { lastUpdateMs: number; ageMs: number; state: string };
  serverTs: number;
  seq: number;
}

/** Unabated consensus update for one (canonicalGameKey, periodTypeId) pair.
 *  Shape matches `UnabatedGameState.consensus` on /api/analytics/game-states
 *  REST so consumers don't need a second mental model. Emitted on ≥1¢ shift
 *  on either prob; backfilled on subscribe for periodTypeId=1 only. */
export interface HydraConsensus {
  canonicalGameKey: string;
  awayTeam: string;     // Unabated's own away/home — swap when landed in canonicalGameKey's homeCode slot
  homeTeam: string;
  periodTypeId: number; // 1 = full game
  periodLabel: string;
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way (Unabated betTypeId 98): the Draw side's fair probability.
   *  Absent/null on binary sports. Sides don't sum to 100 minus vig the way
   *  a binary pair does — render each independently. */
  drawFairProbCents?: number | null;
  contributingBooks: number;
  lastUpdateMs: number;
}

export interface HydraConsensusMsg {
  type: 'consensus';
  consensus: HydraConsensus;
  serverTs: number;
  seq: number;
}

/** Active-sport set meta frame (channel `markets`, HYDRA_API.md §Active
 *  sports). Sorted uppercase hydra wire codes for every sport with ≥1
 *  currently-live market. Sent on every markets/all subscribe (before any
 *  market frame) and re-broadcast on set change. Each frame is the complete
 *  replacement set — never a delta — and is NOT scoped by the consumer's
 *  sports[] filter (discovering sports outside the filter is the point). */
export interface HydraActiveSportsMsg {
  type: 'activeSports';
  sports: string[];
  serverTs: number;
  seq?: number;
}

type HydraServerMsg =
  | HydraMarketMsg
  | HydraOrderbookMsg
  | HydraConsensusMsg
  | HydraActiveSportsMsg
  | { type: 'pong'; clientTs: number; serverTs: number }
  | { type: 'backpressure'; level: number; pending: number; message: string }
  | { type: 'error'; message: string };

// Internal control message from the SharedWorker.
type WorkerControlMsg = { type: '__status'; connected: boolean };

// ── Callbacks ────────────────────────────────────────────────────

export interface HydraCallbacks {
  onMarket: (market: HydraMarket) => void;
  onOrderbook: (ticker: string, book: HydraOrderbook) => void;
  onConsensus: (consensus: HydraConsensus) => void;
  /** Complete replacement set of sports with ≥1 live market (uppercase wire
   *  codes; may include codes g1 doesn't render — consumer filters). Fires
   *  on every (re)subscribe and on set change. */
  onActiveSports?: (sports: string[]) => void;
  onStatus: (connected: boolean) => void;
  /** Latest hydra backpressure level for this connection (0–3). At level
   *  >= 2 hydra suspends orderbook frames entirely (only market + consensus
   *  flow, coalesced) — orderbook-driven UI should show a stale indicator.
   *  Hydra only messages upward transitions; recovery is signalled by
   *  orderbook frames resuming, at which point this fires with 0. */
  onBackpressure?: (level: number) => void;
}

// ── Constants ────────────────────────────────────────────────────

const PING_INTERVAL = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;

// ── Client ───────────────────────────────────────────────────────

export class HydraClient {
  private url: string;
  private callbacks: HydraCallbacks;
  // SharedWorker instance name. Each live feed (firehose, edge:nfl, …) MUST use
  // a distinct name so they get separate worker instances / upstream sockets;
  // two clients sharing a name would share one worker (and one URL).
  private workerName: string;

  // Direct-WS path
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  // SharedWorker path
  private worker: SharedWorker | null = null;
  private port: MessagePort | null = null;
  private workerHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private stallDetectorTimer: ReturnType<typeof setInterval> | null = null;
  // Liveness ping cadence for the SharedWorker reaper. Worker drops
  // sources stale > 30 s; 10 s here gives 3 attempts before reap.
  private readonly WORKER_HEARTBEAT_INTERVAL_MS = 10_000;
  // Tab-side stall detector: if the port goes quiet for this long
  // while we still think we're connected, the worker has stopped
  // talking to us (port reaped during freeze, worker died, etc).
  // Recovery is stop()+startShared() — same path as a bfcache resume.
  //
  // 5 s threshold is aggressive but matches normal traffic: healthy
  // tallies routinely show 100+ frames per 5 s window. Caveat: during
  // truly dead market hours, only the 30 s pong rebroadcast keeps the
  // tab "alive," and 5 s would false-trigger restarts every 5 s; bump
  // to 10–15 s if that ever bites. Check interval 2 s caps detection
  // latency at threshold + interval = ~7 s worst case.
  private readonly TAB_STALL_THRESHOLD_MS = 5_000;
  private readonly TAB_STALL_CHECK_INTERVAL_MS = 2_000;

  // Cached orderbook needs (shared mode only). When stop()+start()
  // rebuilds the SharedWorker port — bfcache resume, stall recovery —
  // we replay these to the new port without waiting for components to
  // re-mount. Worker also caches per-port as belt-and-suspenders.
  private lastDeclaredNeeds: string[] = [];

  private mode: 'shared' | 'direct' | null = null;
  private stopped = false;
  private _connected = false;

  // Latest backpressure level hydra has reported for this connection
  // (0–3). At >= 2 hydra suspends orderbook frames — see HYDRA_API.md
  // "Backpressure warning". Cleared back to 0 when an orderbook frame
  // arrives (the recovery signal — hydra sends no "recovered" message)
  // or when the connection drops (fresh socket = empty buffer = level 0).
  private _backpressureLevel = 0;

  // Stats
  private _marketsReceived = 0;
  private _orderbooksReceived = 0;
  private _lastMessageMs = 0;

  // Current orderbook-channel subscription state (direct-WS mode only;
  // SharedWorker path delegates this to hydraWorker.ts via __needs).
  private directOrderbookNeeds: Set<string> = new Set();
  private directUpstreamOrderbookTickers: Set<string> = new Set();
  private directPendingUnsubscribeTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private readonly UNSUBSCRIBE_GRACE_MS = 30_000;

  constructor(url: string, callbacks: HydraCallbacks, opts: { workerName?: string } = {}) {
    this.url = url;
    this.callbacks = callbacks;
    this.workerName = opts.workerName ?? 'hydra-shared';
  }

  get connected(): boolean { return this._connected; }
  get marketsReceived(): number { return this._marketsReceived; }
  get orderbooksReceived(): number { return this._orderbooksReceived; }
  get lastMessageMs(): number { return this._lastMessageMs; }
  get backpressureLevel(): number { return this._backpressureLevel; }

  private setBackpressureLevel(level: number): void {
    if (level === this._backpressureLevel) return;
    this._backpressureLevel = level;
    this.callbacks.onBackpressure?.(level);
  }

  /** Declare the set of tickers this view needs orderbook data for. In
   *  SharedWorker mode, forwarded to the worker which unions across all
   *  tabs. In direct-WS mode, reconciled locally against the current
   *  upstream subscription with a 30 s grace on removals. */
  setOrderbookNeeds(tickers: string[]): void {
    if (this.mode === 'shared') {
      // Cache before posting so a future startShared() (bfcache resume,
      // stall recovery) can replay the same needs without depending on
      // component remounts.
      this.lastDeclaredNeeds = tickers.slice();
      if (this.port) {
        try {
          this.port.postMessage({ type: '__needs', channel: 'orderbooks', tickers });
        } catch { /* port may be closing — next mount will re-declare */ }
      }
      return;
    }
    // Direct-WS: this client is the sole consumer, so needs == desired.
    this.directOrderbookNeeds = new Set(tickers);
    this.reconcileDirectOrderbookSubscriptions();
  }

  start(): void {
    this.stopped = false;
    if (typeof SharedWorker !== 'undefined') {
      if (this.startShared()) return;
    }
    this.connect();
  }

  /** Re-request a markets snapshot from upstream. Used by `useHydra` when
   *  the tab returns from background (visibilitychange) or the watchdog
   *  detects a silent stall — both cases where the WS thinks it's
   *  connected but `lastMessageMs` has gone quiet. In SharedWorker mode
   *  delegates to the worker, which forwards to upstream; cache entries
   *  get overwritten as frames stream in. */
  requestResnapshot(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__resnapshot' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ op: 'snapshot', channel: 'markets' });
    }
    // If not connected, the next reconnect's onopen will snapshot anyway,
    // so no extra work needed here.
  }

  /** Force a full WS reconnect — used when the freshness signal is
   *  yellow AND the user took an explicit action (chip click, button
   *  press) that says "I want fresh data NOW." Recovers from silently
   *  dead sockets where requestResnapshot() over the same dead socket
   *  is itself a lost message.
   *
   *  Shared mode: posts __reconnect_upstream so the worker rebuilds
   *  the upstream WS for every attached tab. Direct mode: stops then
   *  immediately restarts the per-tab WS. Both paths bypass any
   *  staleness gate — the caller already decided the feed is stale. */
  forceReconnect(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__reconnect_upstream' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    // Direct mode: tear down and immediately re-open. clearTimers +
    // explicit close keeps the existing onclose path quiet enough that
    // we don't double-schedule a reconnect; the new connect() lands
    // before the backoff timer would have.
    this.clearTimers();
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this._connected = false;
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.mode === 'shared') {
      this.stopWorkerHeartbeat();
      this.stopStallDetector();
      if (this.port) {
        try { this.port.postMessage({ type: '__bye' }); } catch { /* port may be closed */ }
        try { this.port.close(); } catch { /* ignore */ }
        this.port = null;
      }
      this.worker = null;
    } else {
      this.clearTimers();
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    }
    this.mode = null;
    this._connected = false;
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
  }

  // ── SharedWorker path ──────────────────────────────────────────

  private startShared(): boolean {
    try {
      this.worker = new SharedWorker(
        new URL('../workers/hydraWorker.ts', import.meta.url),
        { type: 'module', name: this.workerName },
      );
      this.port = this.worker.port;
      this.port.onmessage = (ev: MessageEvent) => {
        const msg = ev.data as HydraServerMsg | WorkerControlMsg | null;
        if (!msg || typeof msg !== 'object') return;
        this._lastMessageMs = Date.now();
        if (msg.type === '__status') {
          this._connected = !!msg.connected;
          // A status flip means the upstream socket changed — any prior
          // backpressure level belonged to the old socket. (If still
          // throttled, the worker's greet() replays the cached
          // backpressure message right after.)
          this.setBackpressureLevel(0);
          this.callbacks.onStatus(this._connected);
          return;
        }
        this.handleMessage(msg);
      };
      this.port.start();
      this.port.postMessage({ type: '__init', url: this.url });
      // Replay cached orderbook needs if any. Happens on stop()+start()
      // recovery (bfcache resume, stall detector) so the new port gets
      // the same subscriptions the old port had without waiting for
      // components to re-mount.
      if (this.lastDeclaredNeeds.length > 0) {
        try {
          this.port.postMessage({
            type: '__needs', channel: 'orderbooks', tickers: this.lastDeclaredNeeds.slice(),
          });
        } catch { /* port may be closing — extremely unlikely on a just-created port */ }
      }
      this.startWorkerHeartbeat();
      this.startStallDetector();
      // Reset the freshness clock so the stall detector gives the new
      // connection a full TAB_STALL_THRESHOLD_MS to deliver its first
      // frame instead of inheriting the stale value from before.
      this._lastMessageMs = Date.now();
      this.mode = 'shared';
      console.log(`[Hydra:${this.workerName}] using SharedWorker (one upstream WS shared across all tabs) → ${this.url}`);
      return true;
    } catch (err) {
      console.warn('[Hydra] SharedWorker init failed, falling back to direct WS:', err);
      this.worker = null;
      this.port = null;
      return false;
    }
  }

  /** Tear down and recreate the SharedWorker port. Used by the tab-side
   *  stall detector and by useHydra's `pageshow` (bfcache restore)
   *  handler. Behaviour is equivalent to stop()+start() restricted to
   *  shared mode — pulled out so callers don't accidentally fall back
   *  to direct WS when SharedWorker is available but currently sick. */
  restartShared(reason: string): void {
    if (this.mode !== 'shared' && this.mode !== null) return;
    console.warn(`[Hydra] tab-side restart: ${reason}`);
    // Local teardown only — do NOT post __bye, since we want the
    // worker's lastKnownNeeds cache to survive for the new port to
    // pick up if the worker ends up reusing this tab's bookkeeping
    // (it won't — new port is a new key — but it's the right intent).
    this.stopWorkerHeartbeat();
    this.stopStallDetector();
    if (this.port) {
      try { this.port.close(); } catch { /* ignore */ }
      this.port = null;
    }
    this.worker = null;
    this._connected = false;
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
    this.startShared();
  }

  // ── Direct-WS path ─────────────────────────────────────────────

  private connect(): void {
    if (this.stopped) return;
    this.mode = 'direct';

    console.log(`[Hydra] connecting to ${this.url}...`);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this._connected = true;
        this.reconnectAttempts = 0;
        console.log('[Hydra] connected');
        this.callbacks.onStatus(true);

        // Markets subscription, scoped to the current sport filter (or broad
        // when none declared / '*'). Orderbooks channel is filtered to
        // declared needs — replay on reconnect from current declared set.
        // Always snapshot: hydra's `markets` channel doesn't auto-snapshot on
        // subscribe (only `orderbooks` does), and skipping the snapshot leaves
        // slow-trading tickers blank until their next live frame.
        this.subscribeMarketsChannel();

        // Unabated consensus channel — sibling to markets/orderbooks on the
        // same WS. Subscribe with no `tickers` filter to receive every game's
        // consensus, then emits on ≥1¢ shift. The explicit snapshot is
        // required: as of 2026-05-29 hydra does NOT auto-snapshot on a bare
        // `subscribe` for this channel (verified by WS probe — bare subscribe
        // returns zero events; only `op: 'snapshot'` triggers the per-game
        // Full Game burst). Same pattern as the markets channel below.
        this.send({ op: 'subscribe', channel: 'unabated' });
        this.send({ op: 'snapshot',  channel: 'unabated' });

        // Clear upstream state (connection just dropped) and let reconcile
        // rebuild the orderbook subscription from the current needs set.
        this.directUpstreamOrderbookTickers.clear();
        for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
        this.directPendingUnsubscribeTimers.clear();
        this.reconcileDirectOrderbookSubscriptions();

        this.startPingLoop();
      };

      this.ws.binaryType = 'arraybuffer';
      this.ws.onmessage = (event) => {
        try {
          const raw = event.data instanceof ArrayBuffer
            ? new TextDecoder().decode(event.data)
            : event.data as string;
          const msg = JSON.parse(raw) as HydraServerMsg;
          this._lastMessageMs = Date.now();
          this.handleMessage(msg);
        } catch (err) {
          console.warn('[Hydra] parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        this._connected = false;
        this.stopPingLoop();
        // An unexpected close while at level >= 2 is almost certainly a
        // backpressure shed — level 3 is an abrupt terminate and the 4002
        // close frame may never be delivered on a saturated socket.
        if (this._backpressureLevel >= 2) {
          console.warn(`[Hydra] close after backpressure level ${this._backpressureLevel} — treating as backpressure shed`);
        }
        this.setBackpressureLevel(0);
        console.log(`[Hydra] disconnected (code=${event.code}, reason=${event.reason || 'none'}, clean=${event.wasClean})`);
        this.callbacks.onStatus(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        console.warn(`[Hydra] ws error — connection to ${this.url} failed. Check: 1) Is Hydra running? 2) Does the URL support WebSocket upgrade? 3) Is port accessible?`);
      };
    } catch (err) {
      console.error('[Hydra] connect failed:', err);
      this.scheduleReconnect();
    }
  }

  private send(msg: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  /** Direct-WS path: reconcile orderbook subscription to match needs.
   *  Adds subscribe immediately; removes are debounced 30 s so rapid
   *  view changes that touch the same tickers don't churn. Mirror of
   *  the SharedWorker-side logic in hydraWorker.ts. */
  private reconcileDirectOrderbookSubscriptions(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const desired = this.directOrderbookNeeds;

    const toAdd: string[] = [];
    for (const ticker of desired) {
      if (!this.directUpstreamOrderbookTickers.has(ticker)) toAdd.push(ticker);
      const pending = this.directPendingUnsubscribeTimers.get(ticker);
      if (pending) {
        clearTimeout(pending);
        this.directPendingUnsubscribeTimers.delete(ticker);
      }
    }

    for (const ticker of this.directUpstreamOrderbookTickers) {
      if (desired.has(ticker)) continue;
      if (this.directPendingUnsubscribeTimers.has(ticker)) continue;
      const timer = setTimeout(() => {
        this.directPendingUnsubscribeTimers.delete(ticker);
        if (this.directOrderbookNeeds.has(ticker)) return;
        this.directUpstreamOrderbookTickers.delete(ticker);
        this.send({ op: 'unsubscribe', channel: 'orderbooks', tickers: [ticker] });
      }, this.UNSUBSCRIBE_GRACE_MS);
      this.directPendingUnsubscribeTimers.set(ticker, timer);
    }

    if (toAdd.length > 0) {
      for (const t of toAdd) this.directUpstreamOrderbookTickers.add(t);
      this.send({ op: 'subscribe', channel: 'orderbooks', tickers: toAdd });
    }
  }

  /** Direct-WS onopen: broad subscribe + snapshot (connectMarketOps — the
   *  markets feed is deliberately unfiltered; see sportFilter.ts). */
  private subscribeMarketsChannel(): void {
    for (const op of connectMarketOps()) this.send(op);
  }

  // ── Message handling ───────────────────────────────────────────

  private handleMessage(msg: HydraServerMsg): void {
    switch (msg.type) {
      case 'market':
        this._marketsReceived++;
        this.callbacks.onMarket(msg.market);
        break;
      case 'orderbook':
        this._orderbooksReceived++;
        // Orderbook frames resuming IS the level-2 recovery signal — hydra
        // suspends them entirely at level >= 2 and sends no "recovered"
        // message. Every frame is a full book, so state self-heals per
        // ticker; for quiet tickers, re-issuing the filtered subscribe
        // triggers hydra's per-ticker auto-snapshot (direct mode only —
        // in shared mode hydraWorker owns the subscription and does this).
        if (this._backpressureLevel >= 2) {
          this.setBackpressureLevel(0);
          if (this.mode === 'direct' && this.directUpstreamOrderbookTickers.size > 0) {
            this.send({
              op: 'subscribe',
              channel: 'orderbooks',
              tickers: [...this.directUpstreamOrderbookTickers],
            });
          }
        }
        this.callbacks.onOrderbook(msg.ticker, msg.orderbook);
        break;
      case 'consensus':
        this.callbacks.onConsensus(msg.consensus);
        break;
      case 'activeSports':
        // Complete replacement set — replace, never merge. Duplicates
        // re-arrive on every subscribe op (worker greet replays too).
        this.callbacks.onActiveSports?.(Array.isArray(msg.sports) ? msg.sports : []);
        break;
      case 'pong':
        // Keepalive acknowledged
        break;
      case 'backpressure':
        console.warn(`[Hydra] backpressure level=${msg.level} pending=${msg.pending}: ${msg.message}`);
        // Track it so orderbook-driven views can show a stale indicator
        // while hydra has our orderbook frames suspended (level >= 2).
        // No proactive reconnect: market + consensus still flow at level
        // 2, and hydra's server-side level2EscalateAfterMs force-
        // disconnects hopeless consumers (abrupt terminate — the 4002
        // close frame may never arrive), which our existing
        // reconnect-with-backoff handles.
        this.setBackpressureLevel(typeof msg.level === 'number' ? msg.level : 0);
        break;
      case 'error':
        console.error(`[Hydra] error: ${msg.message}`);
        break;
    }
  }

  // ── SharedWorker heartbeat ─────────────────────────────────────

  /** Liveness ping to the SharedWorker so its reaper knows this tab is
   *  alive. Without this, a tab that dies without firing pagehide (crash,
   *  OS kill, mobile background evict) leaves its declared orderbook needs
   *  in the worker's union forever, monotonically growing the upstream
   *  subscription. See `hydraWorker.ts` reaper for the matching half. */
  private startWorkerHeartbeat(): void {
    this.stopWorkerHeartbeat();
    this.workerHeartbeatTimer = setInterval(() => {
      if (!this.port) return;
      try { this.port.postMessage({ type: '__heartbeat' }); } catch { /* port closing */ }
    }, this.WORKER_HEARTBEAT_INTERVAL_MS);
  }

  private stopWorkerHeartbeat(): void {
    if (this.workerHeartbeatTimer) {
      clearInterval(this.workerHeartbeatTimer);
      this.workerHeartbeatTimer = null;
    }
  }

  /** Tab-side stall detector. Fires every TAB_STALL_CHECK_INTERVAL_MS;
   *  if we still think we're connected but the port has gone silent for
   *  TAB_STALL_THRESHOLD_MS, the worker has stopped feeding us (port
   *  reaped during a freeze, worker died, etc) and we restart the
   *  SharedWorker connection. The worker's own watchdogs cover the
   *  upstream-silence side; this only catches gaps in the local
   *  port→tab leg that the worker can't observe. Counterpart to the
   *  worker's `reaper.dropped` / `port.resurrect` events. */
  private startStallDetector(): void {
    this.stopStallDetector();
    this.stallDetectorTimer = setInterval(() => {
      if (this.stopped) return;
      if (!this._connected) return;
      if (this._lastMessageMs === 0) return;
      const ageMs = Date.now() - this._lastMessageMs;
      if (ageMs > this.TAB_STALL_THRESHOLD_MS) {
        this.restartShared(`port silent for ${ageMs}ms`);
      }
    }, this.TAB_STALL_CHECK_INTERVAL_MS);
  }

  private stopStallDetector(): void {
    if (this.stallDetectorTimer) {
      clearInterval(this.stallDetectorTimer);
      this.stallDetectorTimer = null;
    }
  }

  // ── Ping / Reconnect (direct-WS path only) ────────────────────

  private startPingLoop(): void {
    this.stopPingLoop();
    this.pingTimer = setInterval(() => {
      this.send({ op: 'ping', ts: Date.now() });
    }, PING_INTERVAL);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    this.reconnectAttempts++;
    console.log(`[Hydra] reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private clearTimers(): void {
    this.stopPingLoop();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
    this.directPendingUnsubscribeTimers.clear();
  }
}
